<template>
  <div
    class="select"
    @click.stop="isActive = type === 'select' ? !isActive : true"
  >
    <div
      :class="'select__input' + (disabled ? ' select__disabled' : '')"
      @keyup.enter.stop="handleEnter($event)"
    >
      <FormInput
        v-model="inputName"
        type="select"
        :refName="refName"
        :placeholder="placeholder"
        :size="size"
        :variant="variant"
        :noLabels="noLabels"
        :label="label"
        :altLabel="altLabel"
        :rounded="rounded"
        :squared="squared"
        :iconSize="inputIconSize"
        :iconColor="iconColor"
        :icon="inputIcon"
        :selected="inputName ? true : false"
        :prefilled="prefilled"
        :error="error"
        :disabled="disabled"
        :status="status"
        :readonly="type === 'select' ? true : false"
        @focused="$emit('focused')"
        @scrolledUp="scrollOptions('up')"
        @scrolledDown="scrollOptions('down')"
        @inputChanged="inputChanged"
      />

      <div
        v-if="type === 'select'"
        :class="['select__icon', 'select__icon--' + iconSize, noLabels === false ? 'select__icon--message' : '']"
      >
        <svg v-if="isActive" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M0 16.67l2.829 2.83 9.175-9.339 9.167 9.339 2.829-2.83-11.996-12.17z"/></svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M0 7.33l2.829-2.83 9.175 9.339 9.167-9.339 2.829 2.83-11.996 12.17z"/></svg>
      </div>
    </div>

    <div :class="['select__dropdown', noLabels === false ? 'select__dropdown--message' : '']">
      <FormDropdown
        :inputValue="inputValue"
        :options="options"
        :type="type"
        :iconSize="iconSize"
        :noIcons="true"
        :active="isActive && !disabled"
        :scrollPosition="scrollPosition"
        :initiated="options.length === 0 && !loading && !timeout && !noResults ? true : false"
        :loading="loading"
        :timeout="timeout"
        :noResults="noResults"
        :height="dropdownHeight"
        @positionChanged="positionChanged"
        @tipChanged="tipChanged"
        @valueChanged="dropdownChanged"
      />
    </div>
  </div>
</template>

<script>
import FormInput from '@/components/form/Input.vue'
import FormDropdown from '@/components/form/Dropdown.vue'

export default {
  components: {
    FormInput,
    FormDropdown
  },
  props: {
    value: {
      type: [Boolean, Object, Number, String],
      default: ''
    },
    options: {
      type: [Boolean, Object, Array],
      default: false
    },
    type: {
      type: String,
      default: 'text'
    },
    refName: {
      type: String,
      default: 'input'
    },
    prefilled: {
      type: Boolean,
      default: false
    },
    noLabels: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ''
    },
    altLabel: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'md',
      validator (values) {
        return ['sm', 'md'].includes(values)
      }
    },
    iconSize: {
      type: String,
      default: 'md',
      validator (values) {
        return ['sm', 'md', 'lg'].includes(values)
      }
    },
    iconColor: {
      type: [Boolean, String],
      default: false
    },
    variant: {
      type: String,
      default: 'dark',
      validator (values) {
        return ['dark', 'light', 'darklight'].includes(values)
      }
    },
    rounded: {
      type: Boolean,
      default: false
    },
    squared: {
      type: Boolean,
      default: false
    },
    icon: {
      type: [Boolean, String, Array],
      default: false
    },
    autocomplete: {
      type: String,
      default: 'off'
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    /*scrollPosition: {
      type: Number,
      default: 0
    },*/
    error: {
      type: [String, Boolean],
      default: false
    },
    status: {
      type: [String, Boolean],
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    timeout: {
      type: Boolean,
      default: false
    },
    noResults: {
      type: Boolean,
      default: false
    },
    dropdownHeight: {
      type: [String, Boolean],
      default: false
    }
  },
  data () {
    return {
      inputValue: this.value.key,
      inputName: this.value.name,
      inputIcon: this.icon,
      inputIconSize: this.iconSize,
      originalInput: false,
      dropdownValue: false,
      isActive: this.autofocus,
      scrollPosition: -1
    }
  },
  methods: {
    positionChanged (value) {
      this.$emit('positionChanged', value)
    },
    tipChanged (value) {
      this.$emit('tipChanged', value)
    },
    inputChanged (value) {
     this.$emit('inputChanged', value)
    },
    dropdownChanged (value, close) {
      if (value && value.key) {
        //this.scrollPosition = figure current index
        this.inputValue = value.key
        this.inputName = value.name
        this.inputIcon = value.icon ? value.icon : this.icon
        this.inputIconSize = value.iconSize ? value.iconSize : this.iconSize
        this.isActive = close === false ? true : false
        this.tipChanged(value.tip)

        if (close === true) {
          this.originalInput = this.input
          this.$emit('valueChanged', value)
          this.$emit('input', value)
        }
      }
    },
    handleEnter (event) {
      if (this.options.length > 0) {
        this.dropdownChanged(this.options[this.scrollPosition], true)
      }
    },
    scrollOptions (direction) {
      if (this.options.length > 0) {
        if (direction === 'down') {
          this.scrollPosition = this.scrollPosition + 1

          if (this.scrollPosition >= this.options.length) {
            this.scrollPosition = -1
          }
        } else if (direction === 'up') {
          this.scrollPosition = this.scrollPosition - 1

          if (this.scrollPosition < -1) {
            this.scrollPosition = (this.options.length - 1)
          }
        }

        if (this.scrollPosition !== -1) {
          this.dropdownChanged(this.options[this.scrollPosition], false)
        } else {
          this.dropdownChanged(this.originalInput, false)
        }

        this.$emit('positionChanged', this.scrollPosition)
      }
    }
  },
  mounted () {
    this.isActive = this.autofocus

    document.addEventListener('keyup', function (event) {
      // TODO: Enter/etc handling @ all form states...
      //console.log(event) // bind scroll and disable
      if (event.keyCode === 38) {
        //console.log('PREVENT THIS')
        //event.preventDefault
        //up
      } else if (event.keyCode === 40) {
        //down
      } else if (event.keyCode === 13) {
        //that.handleEnter()
      } else if (event.keyCode === 27) {
        //that.$store.dispatch('search/setInput', '')
        //that.$store.dispatch('search/setSearchInputStatus', 'inactive')
      } else {
        //that.fetchSuggestions(that.criteria)
        this.$emit('inputChanged', event.target.value)
      }
    }.bind(this))
  },
  unmounted () {
    window.removeEventListener('keyup', this.nextItem)
  },
  watch: {
    value (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.inputName = newValue.name
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/app.scss';

.select {
  position: relative;
  width: 100%;
  cursor: pointer;

  &__disabled {
    pointer-events: none;

  }

  &__input {
    position: relative;
    width: 100%;
    //height: 40px;

    /*@media screen and (min-width: $breakpointLg) {
      height: 44px;
    }*/
  }

  &__icon {
    position: absolute;
    z-index: 6;
    top: 0;
    right: 0;
    fill: rgba($white, 0.25);
    transition: fill 0.2s ease-out;

    &--message {
      top: 26px;

      @media screen and (min-width: $breakpointLg) {
        top: 28px;
      }
    }

    &--activated {
      fill: $white;
    }

    &--sm {
      width: 12px;
      height: 12px;
      padding: 10px 6px;

      @media screen and (min-width: $breakpointLg) {
        width: 14px;
        height: 14px;
        padding: 11px 6px;
      }
    }

    &--md, &--lg {
      width: 14px;
      height: 14px;
      padding: 8px 6px;

      @media screen and (min-width: $breakpointLg) {
        width: 16px;
        height: 16px;
        padding: 9px 6px;
      }
    }
  }

  &__dropdown {
    position: absolute;
    z-index: 999;
    top: 40px;
    width: 100%;

    @media screen and (min-width: $breakpointLg) {
      top: 42px;
    }

    &--message {
      top: 62px;

      @media screen and (min-width: $breakpointLg) {
        top: 66px;
      }
    }
  }
}

</style>